import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import useWidgetData from '../hooks/useWidgetData';
import { TableComponent } from '../../TableComponent_v2';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_INSIGHT_TABLE_MEASURES } from '../../table/measureRegistry';
import WidgetEmptyState from '../WidgetEmptyState';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

// Pre-define icon at module level (babel macro requires static values)
const tableIcon = icon({ name: 'table', family: 'sharp', style: 'light' });

/**
 * InsightTableWidget - Shows insights in a flat table format
 * Supports filtering by insight type (cost, security, reliability)
 */
const InsightTableWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false
}) => {
  const navigate = useNavigate();

  // Stable filters to avoid re-fetch loops when filters is undefined or recreated
  const stableFilters = useMemo(() => filters || [], [filters]);

  // Build config for fetching insights data
  const finalConfig = useMemo(() => ({
    ...config,
    dataSource: {
      type: 'api',
      endpoint: '/get-insights',
      method: 'GET',
      ...config?.dataSource
    }
  }), [config]);

  const { data, isLoading, error } = useWidgetData(finalConfig, stableFilters);

  // Get insight types filter from config (default to all types)
  const selectedInsightTypes = useMemo(() => {
    const types = config?.insightTypes || [];
    // If empty array or undefined, show all types
    return types.length === 0 ? ['cost', 'security', 'reliability'] : types;
  }, [config?.insightTypes]);

  // Filter and prepare data
  const preparedData = useMemo(() => {
    const insights = data?.Insights || [];
    
    if (!insights || insights.length === 0) {
      return [];
    }

    // Filter by insight type based on insight_group
    const filteredInsights = insights.filter(insight => {
      const groupLower = (insight.insight_group || '').toLowerCase();
      
      // Check if any of the selected types match
      return selectedInsightTypes.some(type => {
        if (type === 'cost') {
          return groupLower.includes('cost') || groupLower.includes('costs');
        }
        if (type === 'security') {
          return groupLower.includes('security');
        }
        if (type === 'reliability') {
          return groupLower.includes('reliability') || groupLower.includes('reliable');
        }
        return false;
      });
    });

    // Sort by costs descending
    return filteredInsights.sort((a, b) => (b.costs || 0) - (a.costs || 0));
  }, [data, selectedInsightTypes]);

  // Handle row click - navigate to resource detail (disabled in edit mode)
  const handleRowClick = (rowData) => {
    if (isEditMode) {
      return; // Don't navigate when in edit mode
    }
    if (rowData.resource_md5) {
      navigate("/resource/" + rowData.resource_md5);
    }
  };

  // Get selected measures from config or use defaults
  const selectedMeasures = useMemo(() => {
    return Array.isArray(config?.measures) && config.measures.length > 0
      ? config.measures
      : DEFAULT_INSIGHT_TABLE_MEASURES;
  }, [config?.measures]);

  // Determine empty state content
  const emptyStateContent = useMemo(() => {
    const insights = data?.Insights || [];
    
    if (!insights || insights.length === 0) {
      return {
        title: 'No insights available',
        description: 'Try adjusting your filters or date range'
      };
    }
    
    if (preparedData.length === 0) {
      return {
        title: 'No insights match the selected filters',
        description: 'Try adjusting your filters or date range'
      };
    }
    
    return {
      title: 'No insights available',
      description: 'Try adjusting your filters or date range'
    };
  }, [data, preparedData.length]);

  return (
    <BaseWidget
      widgetId={widgetId}
      title={config.title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      {preparedData && preparedData.length > 0 ? (
        <TableComponent
          data={preparedData}
          grouping={null}
          title={config.title}
          showTitle={config.showTitle}
          measures={selectedMeasures}
          endpoint={config?.dataSource?.endpoint}
          tableType="insight"
          onRowClick={isEditMode ? undefined : handleRowClick}
          showIcons={config.showIcons === true}
          showTotalRow={config.showTotalRow === true}
        />
      ) : !isLoading && !error && (
        <WidgetEmptyState 
          title={emptyStateContent.title}
          description={emptyStateContent.description}
          icon={tableIcon}
        />
      )}
    </BaseWidget>
  );
};

InsightTableWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    showTitle: PropTypes.bool,
    showTotalRow: PropTypes.bool,
    insightTypes: PropTypes.arrayOf(PropTypes.oneOf(['cost', 'security', 'reliability'])),
    measures: PropTypes.arrayOf(PropTypes.string),
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']),
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    })
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool
};

export default InsightTableWidget;

